import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiArrowRight, FiArrowUpRight, FiCalendar, FiCheck, FiFileText, FiMail, FiUserPlus, FiUsers, FiX, FiZap } from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';
import ProCheckoutButton from '../components/ProCheckoutButton';
import { safeExternalUrl } from '../opportunities/previewData';
import { contextualUpgradeCopy } from './upgradeCopy';
import { rememberUpgradeIntent } from './upgradeIntent';
import './upgrade.css';

const OUTCOME_ICONS = { cv: FiFileText, letter: FiMail, referral: FiUserPlus, people: FiUsers };

export default function UpgradeDialog({ user, context = 'default', company, role, onClose, onBeforeCheckout, destination, expiry = false }) {
  const { language } = useLanguage();
  const ar = language === 'ar';
  const id = useId();
  const dialog = useRef(null);
  const message = contextualUpgradeCopy(context, language, { company, role });
  const bookingUrl = safeExternalUrl(process.env.REACT_APP_OFFICE_HOURS_URL) || 'https://calendly.com/jobseeqr-1-on-1/intro-meeting';
  useEffect(() => {
    const element = dialog.current;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    element.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      element.close();
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  const prepareCheckout = async () => {
    await onBeforeCheckout?.();
    rememberUpgradeIntent(user?.uid, destination);
  };
  return createPortal(<dialog ref={dialog} className={`js-context-upgrade${expiry ? ' is-expiry' : message.outcomes ? ' has-outcomes' : ''}`} dir={ar ? 'rtl' : 'ltr'} aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}
    onCancel={(event) => { event.preventDefault(); onClose(); }}
    onClick={(event) => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
    }}>
    <button type="button" className="js-context-upgrade__close" onClick={onClose} aria-label={ar ? 'إغلاق' : 'Close upgrade'}><FiX /></button>
    <span className="js-context-upgrade__icon" aria-hidden="true">{expiry ? <FiCalendar /> : <FiZap />}</span>
    {expiry && <p className="js-context-upgrade__eyebrow">{ar ? 'انتهت تجربتك المجانية' : 'Your trial has ended'}</p>}
    <h2 id={`${id}-title`}>{expiry ? (ar ? 'لنخطّط لخطوتك القادمة معاً.' : "Let's plan your next move together.") : message.heading}</h2>
    <p id={`${id}-description`} className="js-context-upgrade__description">{expiry ? (ar ? 'توقف مساعدك عن البحث. قبل أن تقرر خطوتك التالية، قابل فريقنا لمناقشة بحثك عن عمل.' : "Your Scout has paused. Before you decide what's next, meet with our team to work through your job search.") : message.description}</p>
    {expiry ? <>
      <div className="js-context-upgrade__session"><span>{ar ? 'على حسابنا' : 'On us'}</span><h3>{ar ? 'جلسة إرشاد مهني مع فريق JOBSEEQR' : 'A career-advising session with the JOBSEEQR team'}</h3><p>{ar ? 'أحضر أهدافك وطلباتك وأسئلتك. سنساعدك على تحديد ما يستحق تركيزك.' : "Bring your target roles, applications and questions. We'll help you decide where to focus next."}</p></div>
      <a className="js-context-upgrade__primary" href={bookingUrl} target="_blank" rel="noopener noreferrer" onClick={onClose}>{ar ? 'احجز جلستي المجانية' : 'Book my complimentary session'}<FiArrowUpRight aria-hidden="true" /></a>
      <ProCheckoutButton user={user} onCheckout={prepareCheckout} className="js-context-upgrade__text-action">{ar ? 'جاهز للمتابعة؟ احصل على Pro · 20 دولاراً شهرياً' : 'Ready to continue? Get Pro · $20/month'}</ProCheckoutButton>
    </> : <>
      {message.outcomes ? <ul className="js-context-upgrade__outcomes">{message.outcomes.map(({ icon, title, description }) => {
        const Icon = OUTCOME_ICONS[icon];
        return <li key={title}><span className="js-context-upgrade__outcome-icon" aria-hidden="true"><Icon /></span><div><h3>{title}</h3><p>{description}</p></div></li>;
      })}</ul> : <ul>{message.benefits.map((benefit) => <li key={benefit}><FiCheck aria-hidden="true" /><span>{benefit}</span></li>)}</ul>}
      <div className="js-context-upgrade__price"><strong>{ar ? '20 دولاراً' : '$20'}</strong><span>{ar ? 'شهرياً · جميع مزايا Pro' : '/ month · all Pro features'}</span></div>
      <ProCheckoutButton user={user} onCheckout={prepareCheckout} className="js-context-upgrade__primary">{message.cta}<FiArrowRight className="js-icon--directional" aria-hidden="true" /></ProCheckoutButton>
    </>}
    <button type="button" className="js-context-upgrade__dismiss" onClick={onClose}>{expiry ? (ar ? 'واصل البحث مجاناً' : 'Continue searching for free') : (ar ? 'ليس الآن' : 'Not now')}</button>
  </dialog>, document.body);
}
